/**
 * "Matching this month": how much of a customer's monthly matching limits has
 * been used. Part of Plan & billing in Settings.
 *
 * Reads two endpoints (avrio-backend#193, #194), both scoped by the backend:
 * a Super Admin may read any customer, an Admin only their own.
 * - GET /api/customers/{id}/matching-limits: candidates per job, the monthly
 *   job cap, and the unlock allowance (a count).
 * - GET /api/customers/{id}/matching-usage: this month's jobs matched,
 *   candidates scored, unlocks, and jobs paused or resumed.
 *
 * Customers see counts only (Nachi, 2026-10-02). Dollar figures come back from
 * the backend to a Super Admin only, and only then are they shown here, with
 * an editor for the limits.
 *
 * Never blank: loading, a failed load (with retry), counters that could not be
 * read, and a month with nothing in it yet each say what they are.
 */

import { memo, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Progress, Spin, Typography } from 'antd';
import axios from '../../../../axiosConfig';
import { MatchingLimits, MatchingUsage } from '../../types';
import { monthLabel, summariseUsage, Meter } from './summary';
import MatchingLimitsEditor from './editor';
import styles from './index.module.css';

const { Title, Text } = Typography;

const count = (n: number) => n.toLocaleString('en-US');
const money = (n?: number | null) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }).format(n)
    : '—';

const STROKE: Record<Meter['status'], string> = {
  normal: 'var(--brand-primary, #4c4ff7)',
  near: 'var(--status-warning, #d48806)',
  reached: 'var(--status-error, #cf1322)',
};

const MeterRow = ({
  label,
  meter,
  unit,
}: {
  label: string;
  meter: Meter;
  unit: string;
}) => (
  <div className={styles.meter}>
    <div className={styles.meterHead}>
      <span className={styles.label}>{label}</span>
      <span className={styles.meterValue}>
        {count(meter.used)} of {count(meter.limit)} {unit}
      </span>
    </div>
    <Progress
      percent={meter.percent}
      showInfo={false}
      size="small"
      strokeColor={STROKE[meter.status]}
      aria-label={`${label}: ${meter.percent}% of this month's limit`}
    />
    {meter.status === 'reached' && (
      <Text type="secondary" className={styles.meterNote}>
        Limit reached for this month.
      </Text>
    )}
  </div>
);

interface Props {
  customerId: number;
  isSuperAdmin?: boolean;
}

const MatchingUsageSection = ({ customerId, isSuperAdmin = false }: Props) => {
  const [limits, setLimits] = useState<MatchingLimits | null>(null);
  const [usage, setUsage] = useState<MatchingUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    Promise.all([
      axios.get<MatchingLimits>(`/api/customers/${customerId}/matching-limits`),
      axios.get<MatchingUsage>(`/api/customers/${customerId}/matching-usage`),
    ])
      .then(([l, u]) => {
        if (cancelled) return;
        // A response of the wrong shape is a failed load, not a page crash.
        if (
          typeof u.data?.jobsMatched !== 'number' ||
          typeof u.data?.monthlyJobCap !== 'number' ||
          typeof l.data?.candidatesPerJob !== 'number'
        ) {
          setFailed(true);
          return;
        }
        setLimits(l.data);
        setUsage(u.data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => load(), [load]);

  const header = (
    <Title level={5} id="matching-usage-title" className={styles.title}>
      Matching this month
    </Title>
  );

  if (loading) {
    return (
      <section
        className={styles.section}
        aria-labelledby="matching-usage-title"
      >
        {header}
        <div className={styles.loading}>
          <Spin size="small" />
          <Text type="secondary">Loading this month&rsquo;s matching…</Text>
        </div>
      </section>
    );
  }

  if (failed || !limits || !usage) {
    return (
      <section
        className={styles.section}
        aria-labelledby="matching-usage-title"
      >
        {header}
        <Alert
          type="error"
          showIcon
          message="Could not load matching usage."
          action={
            <Button size="small" onClick={load}>
              Try again
            </Button>
          }
        />
      </section>
    );
  }

  const s = summariseUsage(usage);

  return (
    <section className={styles.section} aria-labelledby="matching-usage-title">
      {header}
      <Text type="secondary" className={styles.intro}>
        {monthLabel(usage.month)}. Limits reset on the 1st of each month (UTC).
      </Text>

      {usage.pausedJobs > 0 && (
        <Alert
          type="warning"
          showIcon
          className={styles.notice}
          message={`${count(usage.pausedJobs)} ${
            usage.pausedJobs === 1 ? 'job is' : 'jobs are'
          } saved and waiting to be matched`}
          description="They start on their own at the start of next month, or sooner if Avrio raises your limit."
        />
      )}
      {usage.resumedFromLastMonth > 0 && (
        <Alert
          type="info"
          showIcon
          className={styles.notice}
          message={`${count(usage.resumedFromLastMonth)} ${
            usage.resumedFromLastMonth === 1 ? 'job' : 'jobs'
          } carried over from last month`}
          description="They were paused at last month's limit and are now being matched."
        />
      )}

      {s.quiet && (
        <Text className={styles.empty}>
          No jobs have been matched yet this month.
        </Text>
      )}

      <MeterRow label="Jobs matched" meter={s.jobs} unit="jobs" />
      {s.unlocks ? (
        <MeterRow
          label="Candidate profiles unlocked"
          meter={s.unlocks}
          unit="unlocks"
        />
      ) : (
        <Alert
          type="info"
          showIcon
          className={styles.notice}
          message="Candidate scores and unlocks can't be shown right now."
          description="Matching is not affected. Refresh later to see them."
        />
      )}

      <dl className={styles.figures}>
        <div>
          <dt className={styles.label}>Candidates scored</dt>
          <dd>
            {s.candidatesScored === null ? '—' : count(s.candidatesScored)}
          </dd>
        </div>
        <div>
          <dt className={styles.label}>Candidates per job</dt>
          <dd>{count(limits.candidatesPerJob)}</dd>
        </div>
        {isSuperAdmin && (
          <div>
            <dt className={styles.label}>Unlock spend (Avrio only)</dt>
            <dd>
              {money(usage.unlockSpend)} of {money(limits.monthlyUnlockBudget)}
            </dd>
          </div>
        )}
      </dl>

      {isSuperAdmin ? (
        <>
          <Text type="secondary" className={styles.note}>
            {limits.isDefault
              ? 'This customer is on the default limits.'
              : `Limits set ${new Date(limits.effectiveFrom ?? '').toLocaleDateString()}.`}
          </Text>
          {editing ? (
            <MatchingLimitsEditor
              customerId={customerId}
              current={limits}
              onCancel={() => setEditing(false)}
              onSaved={(saved) => {
                setLimits(saved);
                setEditing(false);
                load();
              }}
            />
          ) : (
            <Button onClick={() => setEditing(true)}>Edit limits</Button>
          )}
        </>
      ) : (
        <Text type="secondary" className={styles.note}>
          Need higher limits? Contact Avrio.
        </Text>
      )}
    </section>
  );
};

export default memo(MatchingUsageSection);
